import React, { useState, useCallback } from 'react';
import { UploadCloud, FileType, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';

export function ModecMergePage() {
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorStatus, setErrorStatus] = useState<string | null>(null);
    const [successInfo, setSuccessInfo] = useState<{ message: string, filename: string } | null>(null);

    const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const filesArray = Array.from(e.target.files);
            const pdfDocs = filesArray.filter(f => f.name.toLowerCase().endsWith('.pdf'));

            if (pdfDocs.length !== filesArray.length) {
                setErrorStatus("Apenas arquivos PDF são permitidos.");
            } else {
                setErrorStatus(null);
            }

            setSelectedFiles(prev => [...prev, ...pdfDocs]);
        }
    }, []);

    const handleRemoveFile = useCallback((indexToRemove: number) => {
        setSelectedFiles(prev => prev.filter((_, idx) => idx !== indexToRemove));
    }, []);

    const extractBaseName = (filename: string): string | null => {
        const upperFilename = filename.toUpperCase();
        if (upperFilename.endsWith('_PT.PDF')) return filename.slice(0, -7);
        if (upperFilename.endsWith('_EN.PDF')) return filename.slice(0, -7);
        return null;
    };

    const intercalatePages = async (ptBuffer: ArrayBuffer, enBuffer: ArrayBuffer) => {
        const { PDFDocument } = await import('pdf-lib');

        const ptPdf = await PDFDocument.load(ptBuffer);
        const enPdf = await PDFDocument.load(enBuffer);

        const ptNumPages = ptPdf.getPageCount();
        const enNumPages = enPdf.getPageCount();

        let ptPageToRemove = -1;
        let enPageToRemove = -1;

        if (ptNumPages - enNumPages === 1) {
            ptPageToRemove = 2;
        } else if (enNumPages - ptNumPages === 1) {
            enPageToRemove = 2;
        }

        const indicesPt = ptPdf.getPageIndices().filter(i => i !== ptPageToRemove);
        const indicesEn = enPdf.getPageIndices().filter(i => i !== enPageToRemove);

        const sortedPdf = await PDFDocument.create();
        const ptPages = await sortedPdf.copyPages(ptPdf, indicesPt);
        const enPages = await sortedPdf.copyPages(enPdf, indicesEn);

        const maxPages = Math.max(indicesPt.length, indicesEn.length);

        for (let i = 0; i < maxPages; i++) {
            if (i < ptPages.length) {
                sortedPdf.addPage(ptPages[i]);
            }
            if (i < enPages.length) {
                sortedPdf.addPage(enPages[i]);
            }
        }

        return await sortedPdf.save();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedFiles.length === 0) {
            setErrorStatus("Adicione pelo menos um par de PDFs PT/EN para mesclar.");
            return;
        }

        setIsProcessing(true);
        setErrorStatus(null);
        setSuccessInfo(null);

        try {
            // Import libraries dynamically to avoid bundle bloating if user never visits this page
            const JSZip = (await import('jszip')).default;
            const { saveAs } = await import('file-saver');

            const ptFiles = new Map<string, File>();
            const enFiles = new Map<string, File>();

            // Organize pairs
            selectedFiles.forEach((file) => {
                const baseName = extractBaseName(file.name);
                if (baseName) {
                    const key = baseName.toUpperCase().trim();
                    if (file.name.toUpperCase().endsWith('_PT.PDF')) {
                        ptFiles.set(key, file);
                    } else if (file.name.toUpperCase().endsWith('_EN.PDF')) {
                        enFiles.set(key, file);
                    }
                }
            });

            const pairs = new Map<string, { pt: File; en: File }>();
            for (const [key, ptFile] of ptFiles.entries()) {
                if (enFiles.has(key)) {
                    pairs.set(key, { pt: ptFile, en: enFiles.get(key)! });
                }
            }

            if (pairs.size === 0) {
                throw new Error("Nenhum par de documentos PT/EN correspondente foi encontrado nos arquivos selecionados.");
            }

            const zip = new JSZip();

            for (const [baseName, pair] of pairs.entries()) {
                const ptBuffer = await pair.pt.arrayBuffer();
                const enBuffer = await pair.en.arrayBuffer();

                const mergedPdfBytes = await intercalatePages(ptBuffer, enBuffer);

                // Use the original casing from the PT file's base name instead of the uppercase key
                const originalBaseName = extractBaseName(pair.pt.name) || baseName;
                zip.file(`Merged/${originalBaseName}.pdf`, mergedPdfBytes);
            }

            const zipBlob = await zip.generateAsync({ type: "blob" });
            const filename = `Documentos_Modec_${new Date().getTime()}.zip`;
            saveAs(zipBlob, filename);

            setSuccessInfo({ message: "PDFs mesclados e compactados com sucesso localmente!", filename });
            setSelectedFiles([]);

        } catch (error: any) {
            console.error("Erro no processamento local:", error);
            setErrorStatus(error.message || 'Falha ao processar a mesclagem dos arquivos localmente no navegador.');
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in duration-500">
            <div className="bg-white dark:bg-schmersal-gray-dark border-l-4 border-schmersal-blue p-6 rounded-lg shadow-md hover:shadow-sm transition-all duration-300">
                <h1 className="text-2xl font-bold text-schmersal-blue dark:text-schmersal-blue-light mb-2">Projeto Especial Modec</h1>
                <p className="text-schmersal-gray-medium dark:text-schmersal-gray-light">
                    Esta é a ferramenta de mesclagem dedicada. Envie seus pares de documentos PDF (`_PT` e `_EN`) e geraremos os arquivos intercalados.
                </p>
            </div>

            <div className="bg-white dark:bg-schmersal-gray-dark rounded-xl shadow-sm border border-gray-100 dark:border-schmersal-gray-medium overflow-hidden">
                <div className="p-6 border-b border-gray-100 dark:border-schmersal-gray-medium bg-gray-50/50 dark:bg-schmersal-gray-darker">
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center">
                        <UploadCloud className="w-5 h-5 mr-2 text-schmersal-blue" />
                        Upload de Documentos
                    </h2>
                </div>

                <div className="p-6">
                    <form onSubmit={handleSubmit} className="space-y-6">

                        {/* Drag / Drop Área */}
                        <div className="relative border-2 border-dashed border-gray-300 dark:border-schmersal-gray-medium rounded-xl p-8 hover:bg-gray-50 dark:hover:bg-schmersal-gray-darker transition-colors duration-200">
                            <input
                                type="file"
                                multiple
                                accept=".pdf"
                                onChange={handleFileChange}
                                disabled={isProcessing}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10 disabled:cursor-not-allowed"
                            />
                            <div className="flex flex-col items-center justify-center text-center space-y-4">
                                <div className="p-4 bg-blue-50 dark:bg-schmersal-blue/10 rounded-full">
                                    <FileType className="w-10 h-10 text-schmersal-blue dark:text-schmersal-blue-light" />
                                </div>
                                <div>
                                    <p className="font-semibold text-gray-700 dark:text-gray-200 text-lg">
                                        Clique ou arraste seus PDFs aqui
                                    </p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                        Certifique-se de que os nomes terminem com _PT.pdf ou _EN.pdf
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Lista de Arquivos */}
                        {selectedFiles.length > 0 && (
                            <div className="space-y-3 mt-6">
                                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    Arquivos Selecionados ({selectedFiles.length})
                                </h3>
                                <div className="bg-gray-50 dark:bg-schmersal-gray-darker rounded-lg border border-gray-100 dark:border-schmersal-gray-medium max-h-60 overflow-y-auto">
                                    <ul className="divide-y divide-gray-100 dark:divide-schmersal-gray-medium">
                                        {selectedFiles.map((file, idx) => (
                                            <li key={idx} className="flex items-center justify-between p-3 hover:bg-white dark:hover:bg-schmersal-gray-dark/50 transition-colors">
                                                <div className="flex items-center space-x-3 overflow-hidden">
                                                    <FileType className="w-4 h-4 text-schmersal-blue flex-shrink-0" />
                                                    <span className="text-sm text-gray-700 dark:text-gray-200 truncate">
                                                        {file.name}
                                                    </span>
                                                    <span className="text-xs text-gray-500 bg-gray-100 dark:bg-schmersal-gray-dark px-2 py-0.5 rounded-full whitespace-nowrap">
                                                        {(file.size / 1024 / 1024).toFixed(2)} MB
                                                    </span>
                                                </div>
                                                <button
                                                    type="button"
                                                    title="Remover arquivo"
                                                    onClick={() => handleRemoveFile(idx)}
                                                    disabled={isProcessing}
                                                    className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 focus:outline-none transition-colors ml-4 p-1 disabled:opacity-50"
                                                >
                                                    <span className="sr-only">Remover</span>
                                                    &times; {/* Simple cross */}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )}

                        {/* Status Messages */}
                        {errorStatus && (
                            <div className="flex items-center p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm border border-red-100 dark:border-red-800/30">
                                <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                                <p>{errorStatus}</p>
                            </div>
                        )}

                        {successInfo && (
                            <div className="flex items-center p-4 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg text-sm border border-green-100 dark:border-green-800/30">
                                <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                                <div>
                                    <p className="font-semibold">{successInfo.message}</p>
                                    <p className="text-green-500 dark:text-green-300 text-xs mt-0.5">Baixado: {successInfo.filename}</p>
                                </div>
                            </div>
                        )}

                        {/* Ações */}
                        <div className="flex justify-end pt-4 border-t border-gray-100 dark:border-schmersal-gray-medium">
                            <button
                                type="submit"
                                disabled={selectedFiles.length === 0 || isProcessing}
                                className={`flex items-center px-6 py-2.5 rounded-lg text-white font-medium shadow-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-schmersal-blue
                        ${selectedFiles.length === 0 || isProcessing
                                        ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed shadow-none'
                                        : 'bg-schmersal-blue hover:bg-schmersal-blue-light hover:shadow-lg hover:-translate-y-0.5'}`}
                            >
                                {isProcessing ? (
                                    <>
                                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                                        Processando...
                                    </>
                                ) : (
                                    <>
                                        <FileType className="w-5 h-5 mr-2" />
                                        Intercalar e Baixar ZIP
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
